import styled from "@emotion/styled";
import { MeasurementState, Phase, isSpeedtestRunning } from "../../../../features/measurement/measurementSlice";
import LottiePlayer from "../../../../components/elements/spinner/LottiePlayer";
import { useEffect, useState } from "react";
import { MeasurementPercentageKind, getMeasurementPercentageInfo } from "../../../../helper/utils";

import middleAnimationData from "../../../../../assets/spinner/VF_Living_Speechmark_drawn_on_Lottie_Red_1200.json";
import speechmarkSvg from "../../../../../assets/images/splus3/speechmark_148.svg";

const SpeechmarkDiv = styled.div(({ theme }) => ([
    {
        position: "relative",
        width: 148,

        /* for mobile */
        [theme.splus3.breakpoints.mobile]: [
            {
                width: 100,
            }
        ],        
    }
]));

const ProgressTextDiv = styled.div(({ theme }) => ([
    theme.splus3.typography.smallBold,
    theme.splus3.foreground.monochrome600,
    {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        transform: "translate(0px, 8px)",

        /* for mobile */
        [theme.splus3.breakpoints.mobile]: [
            {
                transform: "translate(0px, 4px)",
            }
        ],         
    }
]));

interface ProgressAnimationProps {
    state: MeasurementState;
}

const ProgressAnimation: React.FC<ProgressAnimationProps> = ({ state }) => {
    const [oldKind, setOldKind] = useState(Phase.NotStarted);
    const [percentage, setPercentage] = useState(0);
    const [localPercentage, setLocalPercentage] = useState(0);
    const [totalPercentage, setTotalPercentage] = useState(0);
    const [localStep, setLocalStep] = useState(0);
    const [middleValue, setMiddleValue] = useState(0);

    const percentageUpdateInterval = 0.1;

    const info = getMeasurementPercentageInfo(state);
    if (oldKind !== state.kind) {
        setOldKind(state.kind);
        setLocalPercentage(0);
        setPercentage(totalPercentage);

        switch (info.kind) {
            case MeasurementPercentageKind.NotApplicable:
                setLocalStep(0);
                break;
            case MeasurementPercentageKind.ExactInterval:
                setLocalStep(percentageUpdateInterval / info.interval);
                setTotalPercentage(totalPercentage + info.stageValue);
                break;
            case MeasurementPercentageKind.PossibleInterval:
                setLocalStep(percentageUpdateInterval / info.possibleInterval);
                setTotalPercentage(totalPercentage + info.stageValue);
                setMiddleValue(0.5);
                break;
        }

    }

    useEffect(() => {
        if (!isSpeedtestRunning(state)) {
            if (state.kind === Phase.FinishedNoModem || state.kind === Phase.FinishedWithModem) {
                setPercentage(1);
            }
            return;
        }

        if (info.kind === MeasurementPercentageKind.PossibleInterval && localPercentage >= middleValue) {
            setLocalStep(localStep / 2.0);
            setMiddleValue(0.5 + middleValue / 2.0)
        }

        const handler = window.setTimeout(() => {
            if (info.kind !== MeasurementPercentageKind.NotApplicable) {
                setLocalPercentage(Math.min(1, localPercentage + localStep));
                setPercentage(Math.min(1, percentage + localStep * info.stageValue));
            }
        }, percentageUpdateInterval * 1000);

        return function teadDown() {
            window.clearTimeout(handler);
        }
    }, [state.kind, percentage, localPercentage, totalPercentage]);

    return (
        <SpeechmarkDiv>
            {isSpeedtestRunning(state) ? (
                <LottiePlayer animationData={middleAnimationData} />
            ) : (
                <img width="100%" src={speechmarkSvg.src} />
            )}
            <ProgressTextDiv>{`${Math.round(percentage * 100)}%`}</ProgressTextDiv>
        </SpeechmarkDiv>
    );
}

export default ProgressAnimation;